import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_PRICING_PLAN_TIERS_RESOURCE } from 'src/features/pricingPlan/api/tiers/useListPricingPlanTiers';
import { useUpdatePricingPlanTier } from 'src/features/pricingPlan/api/tiers/useUpdatePricingPlanTier';
import TierFormModal from 'src/features/pricingPlan/components/form/tiers/TierFormModal';
import { PricingPlanTier } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';

interface UpdatePricingPlanTierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTier: PricingPlanTier;
    children?: React.ReactNode;
}

function UpdatePricingPlanTierFormModal<TData>({
    className,
    semantics,
    children,
    selectedTier,
}: UpdatePricingPlanTierFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdatePricingPlanTier({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_TIERS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.updateTierSuccess),
                description: formatMessage(pricingPlanMessages.updateTierSuccessDescription),
            });
        },
    });

    return (
        <TierFormModal
            selectedTier={selectedTier}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ planId, tierId: selectedTier.id, parkingId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editTierSubtitle)}
        >
            {children}
        </TierFormModal>
    );
}

export default UpdatePricingPlanTierFormModal;
